import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Space, Select, Tag, Typography, Row, Col, message, Statistic, Progress, Modal, Form, Slider, Radio, Popconfirm, List, Empty,
} from 'antd';
import { ShareAltOutlined, PlusOutlined, PlayCircleOutlined, StopOutlined } from '@ant-design/icons';
import { apiGet, apiPost } from '../services/api';

const { Title, Text } = Typography;

// 商务风格配色
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  // 兼容旧代码的别名
  cyan: '#1677ff',
  purple: '#722ed1',
  pink: '#ff4d4f',
  green: '#52c41a',
  orange: '#faad14',
  yellow: '#faad14',
  darkBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

interface MutualFollowTask {
  id: number; taskId: string; strategy: string; sourceGroupId: number; targetGroupId?: number; probability?: number;
  totalCount: number; completedCount: number; failedCount: number; status: string; createTime: string;
}
interface BotGroup { id: number; groupName: string; memberCount?: number; }
interface SocialStats { totalRelations: number; mutualCount: number; followCount: number; }

// 简洁页面标题组件
const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({ icon, title, subtitle, color = COLORS.primary }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 72,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

// 简洁卡片组件
const SimpleCard: React.FC<{ title?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties; accentColor?: string }> = ({ title, extra, children, style, accentColor = COLORS.primary }) => (
  <Card
    title={title ? <Text style={{ fontWeight: 600, fontSize: 16 }}>{title}</Text> : undefined}
    extra={extra}
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      overflow: 'hidden',
      ...style,
    }}
    styles={{
      header: title ? {
        borderBottom: `1px solid ${COLORS.borderDark}`,
        background: `${accentColor}08`,
      } : undefined,
      body: { padding: 24 },
    }}
  >
    {children}
  </Card>
);

// 简洁统计卡片
const StatCard: React.FC<{ title: string; value: number; color: string }> = ({ title, value, color }) => (
  <Card style={{
    background: COLORS.cardBg,
    border: `1px solid ${COLORS.borderDark}`,
    borderRadius: 12,
  }}>
    <Statistic
      title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{title}</Text>}
      value={value}
      valueStyle={{ color: color, fontWeight: 700 }}
    />
  </Card>
);

const SocialNetwork: React.FC = () => {
  const [tasks, setTasks] = useState<MutualFollowTask[]>([]);
  const [groups, setGroups] = useState<BotGroup[]>([]);
  const [stats, setStats] = useState<SocialStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [form] = Form.useForm();

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<any>(`/api/social/mutual-follow/tasks`);
      setTasks(Array.isArray(data) ? data : (data?.data || []));
    } catch (e: any) { message.error('加载任务失败: ' + e.message); setTasks([]); }
    finally { setLoading(false); }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const data = await apiGet<any>(`/api/bot/group`);
      setGroups(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) { setGroups([]); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiGet<any>(`/api/social/stats`);
      setStats(data?.data || data);
    } catch (e) { console.error('加载统计失败', e); }
  }, []);

  useEffect(() => { fetchTasks(); fetchGroups(); fetchStats(); }, [fetchTasks, fetchGroups, fetchStats]);

  const handleCreateTask = async (values: any) => {
    try {
      let url = '', body: any = {};
      if (values.strategy === 'all') { url = `/api/social/mutual-follow/full`; body = { groupId: values.sourceGroupId }; }
      else if (values.strategy === 'probability') { url = `/api/social/mutual-follow/probability`; body = { groupId: values.sourceGroupId, probability: values.probability }; }
      else if (values.strategy === 'group') { url = `/api/social/mutual-follow/cross-group`; body = { sourceGroupId: values.sourceGroupId, targetGroupId: values.targetGroupId, mutual: true }; }
      const data = await apiPost<any>(url, body);
      if (data.id) { setShowCreateModal(false); form.resetFields(); fetchTasks(); message.success('创建成功'); }
    } catch (e: any) { message.error('创建任务失败: ' + e.message); }
  };

  const handleExecuteTask = async (taskId: number) => {
    setExecuting(true);
    try {
      const data = await apiPost<any>(`/api/social/mutual-follow/task/${taskId}/execute`, { batchSize: 100 });
      message.success(`执行完成: 处理了 ${data.executed || 0} 对关系`);
      fetchTasks(); fetchStats();
    } catch (e: any) { message.error('执行失败: ' + e.message); }
    finally { setExecuting(false); }
  };

  const handleCancelTask = async (taskId: number) => {
    try {
      await apiPost<any>(`/api/social/mutual-follow/task/${taskId}/cancel`, {});
      fetchTasks(); message.success('已取消');
    } catch (e: any) { message.error('取消失败: ' + e.message); }
  };

  const getStrategyLabel = (s: string) => ({ all: '全量互关', probability: '概率互关', group: '跨组互关' }[s] || s);
  const getStatusColor = (s: string) => ({ pending: '#6b7280', running: COLORS.cyan, completed: COLORS.green, failed: COLORS.pink }[s] || '#6b7280');
  const getStatusLabel = (s: string) => ({ pending: '等待中', running: '执行中', completed: '已完成', failed: '已取消' }[s] || s);
  const getProgress = (t: MutualFollowTask) => t.totalCount === 0 ? 0 : Math.round((t.completedCount / t.totalCount) * 100);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader icon={<ShareAltOutlined />} title="社交网络" subtitle="管理机器人间的社交关系" color={COLORS.purple} />

      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}><StatCard title="总关系数" value={stats.totalRelations} color="rgba(0, 0, 0, 0.85)" /></Col>
          <Col span={8}><StatCard title="互关关系" value={stats.mutualCount} color={COLORS.green} /></Col>
          <Col span={8}><StatCard title="单向关注" value={stats.followCount} color={COLORS.cyan} /></Col>
        </Row>
      )}

      <SimpleCard
        title={<Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>互关任务</Text>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreateModal(true)}>新建互关任务</Button>}
        accentColor={COLORS.purple}
      >
        <List loading={loading} dataSource={tasks} locale={{ emptyText: <Empty description={<Text style={{ color: '#6b7280' }}>暂无任务</Text>} /> }} renderItem={task => (
          <List.Item
            style={{
              padding: 20,
              marginBottom: 12,
              background: COLORS.darkBg,
              borderRadius: 12,
              border: `1px solid ${COLORS.borderDark}`,
            }}
            actions={
              (task.status === 'pending' || task.status === 'running') ? [
                <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => handleExecuteTask(task.id)} loading={executing} style={{ background: COLORS.success, borderColor: COLORS.success }}>执行</Button>,
                <Popconfirm title="确定要取消此任务吗？" onConfirm={() => handleCancelTask(task.id)}>
                  <Button icon={<StopOutlined />} danger>取消</Button>
                </Popconfirm>
              ] : []
            }
          >
            <List.Item.Meta
              title={
                <Space>
                  <Tag style={{ background: `${COLORS.purple}15`, border: `1px solid ${COLORS.purple}33`, color: COLORS.purple }}>{getStrategyLabel(task.strategy)}</Tag>
                  <Tag style={{ background: `${getStatusColor(task.status)}15`, border: `1px solid ${getStatusColor(task.status)}33`, color: getStatusColor(task.status) }}>{getStatusLabel(task.status)}</Tag>
                </Space>
              }
              description={
                <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
                  <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>任务ID: {task.taskId} | 进度: <span style={{ color: COLORS.primary, fontWeight: 600 }}>{task.completedCount}</span> / {task.totalCount} {task.failedCount > 0 && <span style={{ color: COLORS.error }}>| 失败: {task.failedCount}</span>}</Text>
                  <Progress
                    percent={getProgress(task)}
                    strokeColor={{ '0%': COLORS.primary, '100%': COLORS.success }}
                    trailColor={COLORS.borderDark}
                    strokeWidth={10}
                  />
                </Space>
              }
            />
          </List.Item>
        )} />
      </SimpleCard>

      <Modal
        title={<Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>新建互关任务</Text>}
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleCreateTask} layout="vertical" initialValues={{ strategy: 'all', probability: 50 }} style={{ marginTop: 24 }}>
          <Form.Item label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>互关策略</Text>} name="strategy">
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="all"><Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>全量互关 (分组内所有成员互相关注)</Text></Radio>
                <Radio value="probability"><Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>概率互关 (按概率建立关系)</Text></Radio>
                <Radio value="group"><Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>跨组互关 (两个分组间互关)</Text></Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>源分组</Text>} name="sourceGroupId" rules={[{ required: true, message: '请选择分组' }]}>
            <Select placeholder="请选择分组" options={groups.map(g => ({ value: g.id, label: `${g.groupName} (${g.memberCount || 0}人)` }))} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.strategy !== curr.strategy}>
            {({ getFieldValue }) => getFieldValue('strategy') === 'probability' && (
              <Form.Item label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>互关概率: <span style={{ color: COLORS.secondary, fontWeight: 600 }}>{getFieldValue('probability')}%</span></Text>} name="probability"><Slider min={1} max={100} marks={{ 1: '1%', 50: '50%', 100: '100%' }} /></Form.Item>
            )}
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.strategy !== curr.strategy || prev.sourceGroupId !== curr.sourceGroupId}>
            {({ getFieldValue }) => getFieldValue('strategy') === 'group' && (
              <Form.Item label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>目标分组</Text>} name="targetGroupId" rules={[{ required: true, message: '请选择目标分组' }]}>
                <Select placeholder="请选择分组" options={groups.filter(g => g.id !== getFieldValue('sourceGroupId')).map(g => ({ value: g.id, label: `${g.groupName} (${g.memberCount || 0}人)` }))} />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setShowCreateModal(false)}>取消</Button>
              <Button type="primary" htmlType="submit">创建任务</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SocialNetwork;
